<template>
  <div>
    <b-row>
      <b-col>
        <draggable
          v-model="driversList"
          v-bind="options"
          :move="onMove"
          ref="draggable"
          class="d-inline"
          :disabled="isModeling"
          draggable=".driver-item"
        >
          <div v-for="(element, index) in driversList" class="driver-item d-inline-block mb-2" :key="index">
            <div
              class="p-0 mr-2 d-inline-block font-weight-bold rounded selected-drivers"
              :style="setDriverStyle(element.driver)"
            >
              <div
                class="text-center d-inline-block indicator-default-style"
                :style="setIndicatorNumberStyle(element.driver)"
              >
                {{ index + 1 }}
              </div>
              <span class="ml-2 mr-2">
                {{ getDriverCode(element.driver) }}
              </span>

              <editable-badge
                :limit="100"
                :badge-id="index"
                :variant="'secondary'"
                :editable="canEditBadges"
                :callback="preventCharsCode"
                :badge-value="getDriverValue(element.weight)"
                :badge-class="['mr-2', 'align-baseline', 'driver-editable-badge']"
                @update-value="updateCustomDriverWeight(element, ...arguments)"
              >
                <span>{{ getDriverValue(element.weight) }}</span>
              </editable-badge>

              <span v-if="!isModeling" class="ml-1 mr-2 pointer" @click="removeDriver(element.driver)">
                <i class="fa fa-times" aria-hidden="true"></i>
              </span>
            </div>
            <div v-if="showWeightAlert && element.weight === 0" class="mr-2 float-right alert-icon-padding">
              <font-awesome-icon class="small text-danger" icon="exclamation-circle" />
            </div>
          </div>

          <div
            v-if="!isDriversLimitReached && !isModeling"
            slot="footer"
            role="group"
            key="footer"
            :empty-insert-threshold="500"
            class="driver-item d-inline-block"
          >
            <div class="dashed selected-drivers p-0 rounded font-weight-bold mb-2 mr-2 d-inline-block">
              <div class="text-center d-inline-block driver-number">1</div>
            </div>
          </div>
        </draggable>
      </b-col>

      <b-col cols="auto" align-self="center">
        <template v-if="!isModeling">
          <b-button
            variant="link"
            class="clear-all"
            @click="removeAll"
            :disabled="driversList && driversList.length === 0"
          >
            {{ $t('scoringProfiles.removeAll') }}
            <i class="fa fa-trash small ml-1 trash-icon"></i>
          </b-button>
        </template>
      </b-col>
    </b-row>

    <b-row no-gutters>
      <b-col>
        <div class="border-bottom mb-3"></div>
      </b-col>
    </b-row>

    <b-row no-gutters v-if="indicatorsCategories.length > 0">
      <b-col>
        <b-row no-gutters v-for="(item, index) in indicatorsCategoriesSorted" :key="index">
          <b-col>
            <custom-accordion
              :item="item"
              :callout-style="setCalloutStyle(item.toUpperCase())"
              @button-clicked="getCollapseId"
            >
              <template v-slot:accordion-content>
                <draggable v-model="indicators[item]" v-bind="options" :move="onMove">
                  <div
                    v-for="driver in indicators[item]"
                    :key="driver.code"
                    :style="getDriverStyle(item.toUpperCase())"
                    :class="draggableDriversClass(driver)"
                    @dblclick="addDriver(driver)"
                  >
                    {{ getDriverCode(driver) }}
                  </div>
                </draggable>
              </template>
            </custom-accordion>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { SCORING_DEFINITION_TYPES } from '../../settings/scoringProfilesSettings'
import { preventCharsCode } from '../../utils/formatter'
import EditableBadge from '../EditableFields/EditableBadge.vue'
import CustomAccordion from '../../components/Lib/CustomAccordion.vue'

export default {
  components: {
    draggable,
    CustomAccordion,
    EditableBadge,
  },

  props: {
    drivers: {
      type: Array,
      required: true,
    },
    driversLimit: {
      type: Number,
      required: true,
    },
    weightType: {
      type: String,
      required: true,
    },
    canEditBadges: {
      type: Boolean,
      required: true,
      default: false,
    },
    indicators: {
      type: Object,
      required: true,
    },
    indicatorsCategories: {
      type: Array,
      required: true,
    },
    sequenceWeightsDecay: {
      type: Number,
      required: true,
    },
    isModeling: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      collapseId: null,
      options: {
        animation: 0,
        disabled: false,
        group: { name: 'drivers', pull: 'clone' },
      },
    }
  },

  computed: {
    driversList: {
      get() {
        return this.drivers
      },
      set(newValue) {
        this.updateDriversListSorting(newValue)
      },
    },

    isDriversLimitReached: {
      get() {
        return Array.isArray(this.driversList) && this.driversList.length >= this.driversLimit
      },
    },

    indicatorsCategoriesSorted: {
      get() {
        return this.indicatorsCategories.sort((a, b) => {
          return this.$t('drivers.categories.' + a.toUpperCase()).localeCompare(
            this.$t('drivers.categories.' + b.toUpperCase())
          )
        })
      },
    },

    showWeightAlert() {
      return this.weightType === SCORING_DEFINITION_TYPES.custom
    },
  },

  methods: {
    preventCharsCode,

    getCollapseId(value) {
      this.collapseId = value
    },

    getDriverCode(item) {
      return this.isModeling ? 'Driver' : this.$t('drivers.codes.' + item.toUpperCase())
    },

    getDriverStyle(category) {
      const color = 'var(--categoryPrimaryColor' + category + ')'
      return 'color: ' + color + ';border: 1px solid ' + color
    },

    getDriverCategoryFromCode(driverCode) {
      for (const category of this.indicatorsCategories) {
        const temp = this.indicators[category].find((k) => k === driverCode)
        if (temp !== undefined) return category
      }
      return ''
    },

    getDriverValue(value) {
      if (this.weightType === SCORING_DEFINITION_TYPES.custom) return this.$n(value, 'percent')

      return value >= 0.0001 ? this.$n(value, 'percent') : '< 0,01%'
    },

    setDriverStyle(item) {
      const color = 'color: var(--gray8); border: 1px solid var(--gray8)'
      return this.isModeling ? color : this.getDriverStyle(this.getDriverCategoryFromCode(item).toUpperCase()) + ';'
    },

    setIndicatorNumberStyle(element) {
      return this.isModeling
        ? 'background-color: var(--gray4); border-right: 1px solid var(--gray10);'
        : 'background-color: var(--categoryPrimaryColor' + this.getDriverCategoryFromCode(element).toUpperCase() + ');'
    },

    onMove({ relatedContext, draggedContext, dragged }) {
      const draggedElement = draggedContext.element
      if (dragged.classList.contains('draggable-indicators-disabled')) {
        return false
      }
      if (relatedContext.component !== this.$refs.draggable) {
        return false
      }
      if (this.isDriversLimitReached && dragged.classList.contains('draggable-indicators')) {
        return false
      }
      const driverAlreadyAdded = this.driversList.some((item) => item.driver === draggedElement)
      return !driverAlreadyAdded
    },

    removeAll() {
      this.$emit('on-remove-all')
    },

    removeDriver(value) {
      this.$emit('on-remove-driver', value)
    },

    addDriver(value) {
      if (this.isDriversLimitReached || this.driversList.some((item) => item.driver === value)) {
        return false
      }
      this.$emit('on-add-driver', value)
    },

    updateCustomDriverWeight(element, data) {
      this.$emit('on-update-custom-driver-weight', !this.isModeling ? element.driver : element.family, data.value)
    },

    updateDriversListSorting(value) {
      this.$emit('on-update-drivers-list-sorting', value)
    },

    draggableDriversClass(item) {
      return [
        'draggable-indicators',
        Array.isArray(this.driversList) &&
        this.driversList.length > 0 &&
        this.driversList.some((k) => k.driver === item)
          ? 'draggable-indicators-disabled'
          : '',
      ]
    },

    setCalloutStyle(category) {
      const color = 'var(--categoryPrimaryColor' + category + ')'
      return 'border-left: 3px solid ' + color + ';'
    },
  },
}
</script>

<style lang="scss" scoped>
.draggable-indicators-disabled {
  opacity: 0.5;
}

.indicator-default-style {
  opacity: 0.5;
  color: #000;
  padding: 3px 4px;
}

.driver-number {
  padding: 3px 4px;
}

.trash-icon {
  font-size: 10px;
}

.driver-editable-badge {
  margin-bottom: 4px !important;
}

.text-orange {
  color: #ffa500 !important;
}

.dashed {
  border: 1px dashed rgb(219, 227, 237);
  color: transparent;
}

.clear-all {
  font-size: 0.8rem !important;
  text-decoration: none !important;
}

.alert-icon-padding {
  padding-top: 0.2rem;
}

.selected-drivers {
  cursor: move;
  padding: 5px 15px;
  min-height: 25px;
  min-width: 100px;
}

.draggable-indicators {
  padding: 3px 15px;
  margin: 3px;
  cursor: move;
  border-radius: 4px;
  display: inline-block;
  font-weight: bold;
}
</style>
